import ViewportInterface, { DEFAULT_VIEW_OFFSETS } from './ViewportInterface.js';

const AecModelData = Autodesk.AEC.AecModelData;

export default class AecModelDataViewport extends ViewportInterface {

    /**
     * @param {...*} args - Arguments forwarded to {@link ViewportInterface}.
     *   options.sheetNode {Autodesk.Viewing.BubbleNode} - The parent sheet BubbleNode.
     *   options.aecModelData {Object} - The AecModelData instance for this model.
     */
    constructor(...args) {
        super(...args);

        this.sheetNode = this.options.sheetNode;
        this.aecModelData = this.options.aecModelData;
    }

    /**
     * Returns the unique viewport guid.
     * @returns {string}
     */
    getGuid() {
        return this.viewportRaw.viewportGuid;
    }

    /**
     * Whether the 2D-to-3D matrix is in model-local coordinates (true) or world coordinates (false).
     * Always true for AecModelData viewports.
     * @returns {boolean}
     */
    isMatrixInLocalCoords() {
        return true;
    }

    /**
     * Returns the 2D bounding box of the viewport on the sheet.
     * Viewports in AecModelData are stored in feet, regardless of the model's actual units.
     * unitScale must be provided to convert to model units. It cannot be provided at construction
     * time because model units are only known after the model is loaded.
     * @param {number} unitScale - Scale factor to convert from feet to model units. Required.
     * @returns {Autodesk.Viewing.Math.Box2 | null}
     */
    getViewportBounds(unitScale) {
        if (!unitScale) {
            console.error('Unit scale must be provided');
            return null;
        }

        return AecModelData.getViewportBounds(this.viewportRaw, unitScale);
    }

    /**
     * Returns the 4x4 matrix that transforms 2D sheet coordinates into 3D model/world coordinates.
     * For floor plans, the Z position is overridden with the level elevation.
     * @param {number} unitScale - Scale factor to convert from feet to model units. Required.
     * @returns {Autodesk.Viewing.Math.Matrix4 | null}
     */
    get2DTo3DMatrix(unitScale) {
        if (!unitScale) {
            console.error('Unit scale must be provided');
            return null;
        }

        let matrix = AecModelData.get2DTo3DMatrix(this.viewportRaw, unitScale);

        // For floorPlans, override the height with the level elevation.
        if (this.isFloorPlan()) {
            const level = this.findLevelForViewport();

            // Take elevation from level
            if (level) {
                const zOffsetHack = 1 / 120; // Offset to place slightly above floor
                const pos = new THREE.Vector3();
                pos.setFromMatrixPosition(matrix);
                pos.z = level.elevation + zOffsetHack;
                matrix = matrix.clone().setPosition(pos);
            }
        }

        return matrix;
    }

    /**
     * Returns the 2D sheet BubbleNode that contains this viewport.
     * @returns {Promise<Autodesk.Viewing.BubbleNode>}
     */
    async getParentSheetNode() {
        return this.sheetNode;
    }

    /**
     * Returns the 3D BubbleNodes related to this viewport.
     * @returns {Promise<Autodesk.Viewing.BubbleNode[]>}
     */
    async get3DNodesForViewport() {
        const views = this.sheetNode.getRootNode().search(Autodesk.Viewing.BubbleNode.MODEL_NODE);

        if (!views.length) {
            console.error("That shouldn't happen - No AEC views");
        }

        return views;
    }

    /**
     * Finds the building level that contains this viewport.
     * @returns {{elevation: number, guid?: string} | null}
     */
    findLevelForViewport() {
        return AecModelData.findLevelForViewport(this.aecModelData, this.viewportRaw);
    }

    /**
     * Checks whether this viewport falls within the given building level.
     * @param {{guid: string}} level - Level to test against.
     * @returns {boolean}
     */
    isViewportInLevel(level) {
        const linkedLevelGuid = AecModelData.getLinkedLevel(this.viewportRaw);
        return level.guid === linkedLevelGuid;
    }

    /**
     * Whether this viewport supports 2D-to-3D coordinate transformation.
     * @returns {boolean}
     */
    supports2DTo3DTransform() {
        return AecModelData.supports2DTo3DTransform(this.viewportRaw);
    }

    /**
     * Whether this viewport faces up (top-down view).
     * @returns {boolean}
     */
    isTopViewViewport() {
        return AecModelData.TopViewViewportTypes.includes(this.viewportRaw.viewType);
    }

    /**
     * Whether this viewport represents a floor plan.
     * @returns {boolean}
     */
    isFloorPlan() {
        return this.viewportRaw.viewType === 'FloorPlan';
    }

    /**
     * Returns the viewport type string (e.g. 'FloorPlan', 'Section', 'Elevation', 'Detail', 'CeilingPlan').
     * @returns {string | null}
     */
    getViewType() {
        return this.viewportRaw.viewType || null;
    }

    /**
     * Returns view clipping offsets from the cut plane, in meters.
     * View range planes in AecModelData are defined relative to a level (levelGuid + offset in feet).
     * We resolve each plane to an absolute elevation and return distances from the cut plane.
     * Falls back to DEFAULT_VIEW_OFFSETS if view range data is unavailable.
     * @returns {{top: number, bottom: number} | null}
     */
    getViewOffsets() {
        // return explicitly defined view offsets if available
        if (this.viewportRaw.viewOffsets) {
            return this.viewportRaw.viewOffsets;
        }

        // try to calculate view offsets from view range data if available
        const viewRange = this.viewportRaw.extensions?.viewRange;
        if (viewRange?.topClipPlane && viewRange?.bottomClipPlane && viewRange?.cutPlane) {
            const feetToMeters = 0.3048;
            const levels = this.aecModelData?.levels;

            const resolveElevation = (plane) => {
                if (!plane.levelGuid || !levels) {
                    return null;
                }
                const level = levels.find(l => l.guid === plane.levelGuid);
                if (!level) {
                    return null;
                }
                return level.elevation + plane.offset;
            };

            const cutElevation = resolveElevation(viewRange.cutPlane);
            const topElevation = resolveElevation(viewRange.topClipPlane);
            const bottomElevation = resolveElevation(viewRange.bottomClipPlane);

            if (cutElevation !== null && topElevation !== null && bottomElevation !== null) {
                return {
                    top: (topElevation - cutElevation) * feetToMeters,
                    bottom: (cutElevation - bottomElevation) * feetToMeters,
                };
            }
        }

        // return default values based on view type
        const viewType = this.getViewType();
        return viewType ? (DEFAULT_VIEW_OFFSETS[viewType] || null) : null;
    }
}
